/**
 * API Client
 * Core API client with interceptors, error handling, and token management
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_CONFIG } from './config';
import { ApiResponse, ApiError } from './types';

class ApiClient {
  private client: AxiosInstance;
  private refreshTokenPromise: Promise<string> | null = null;

  constructor() {
    // Create axios instance with initial baseURL
    const baseURL = API_CONFIG.VERSION 
      ? `${API_CONFIG.BASE_URL}/${API_CONFIG.VERSION}`
      : API_CONFIG.BASE_URL;
    
    this.client = axios.create({
      baseURL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Setup interceptors
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /**
   * Update base URL dynamically
   */
  public updateBaseURL(): void {
    const baseURL = API_CONFIG.VERSION 
      ? `${API_CONFIG.BASE_URL}/${API_CONFIG.VERSION}`
      : API_CONFIG.BASE_URL;
    this.client.defaults.baseURL = baseURL;
  }

  /**
   * Setup request interceptor to add Basic Auth and API key
   */
  private setupRequestInterceptor(): void {
    this.client.interceptors.request.use(
      (config) => {
        // Update base URL dynamically from localStorage
        const baseURL = API_CONFIG.VERSION 
          ? `${API_CONFIG.BASE_URL}/${API_CONFIG.VERSION}`
          : API_CONFIG.BASE_URL;
        config.baseURL = baseURL;
        
        // Determine which API is being used
        const useAltApi = localStorage.getItem('use_alt_api') === 'true';
        
        // Check if this is a player roadmap detail endpoint
        const isPlayerRoadmapEndpoint = config.url?.includes('/user/player/') && config.url?.includes('/roadmap/');
        
        // Log request details BEFORE sending
        console.log('=== API REQUEST (BEFORE SENDING) ===');
        console.log('URL:', config.url);
        console.log('Base URL:', baseURL);
        console.log('Full URL:', `${baseURL}${config.url}`);
        console.log('Method:', config.method?.toUpperCase() || 'GET');
        console.log('Using Alt API:', useAltApi);
        console.log('Is Player Roadmap Endpoint:', isPlayerRoadmapEndpoint);
        console.log('Params:', config.params);
        console.log('Current Headers:', { ...config.headers });
        
        if (useAltApi) {
          // Alternative API: Use Basic Auth
          const basicAuth = this.getBasicAuth();
          if (basicAuth && config.headers) {
            config.headers.Authorization = `Basic ${basicAuth}`;
            console.log(`[API] Adding Basic Auth token for: ${config.url}`);
          } else {
            console.warn('[API] Basic Auth token not found for request:', config.url);
          }
        } else {
          // Normal API: Use Basic Auth for most endpoints, X-API-KEY header for player roadmap
          if (isPlayerRoadmapEndpoint) {
            // Player roadmap detail endpoint: Use X-API-KEY header with API token
            if (config.headers) {
              config.headers['X-API-KEY'] = API_CONFIG.API_TOKEN;
              console.log(`[API] Adding X-API-KEY header for player roadmap: ${config.url}`);
              console.log(`[API] X-API-KEY value: ${API_CONFIG.API_TOKEN}`);
            }
          } else {
            // Other endpoints: Use Basic Auth
            const basicAuth = this.getBasicAuth();
            if (basicAuth && config.headers) {
              config.headers.Authorization = `Basic ${basicAuth}`;
              console.log(`[API] Adding Basic Auth token for: ${config.url}`);
            } else {
              console.warn('[API] Basic Auth token not found for request:', config.url);
            }
          }
        }
        
        // Log final headers after adding auth
        console.log('Final Headers:', { ...config.headers });
        console.log('=====================================');
        
        // Add request timestamp for logging
        config.metadata = { startTime: new Date() };
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Setup response interceptor to handle errors and token refresh
   */
  private setupResponseInterceptor(): void {
    this.client.interceptors.response.use(
      (response) => {
        // Log response time
        if (response.config.metadata) {
          const endTime = new Date();
          const duration = endTime.getTime() - response.config.metadata.startTime.getTime();
          console.log(`API Call: ${response.config.url} - ${duration}ms`);
        }
        
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Handle 401 Unauthorized - Basic Auth failed
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Basic Auth failed, logout user
          this.handleAuthError();
          return Promise.reject(this.normalizeError(error));
        }

        // Handle other errors
        return Promise.reject(this.normalizeError(error));
      }
    );
  }

  /**
   * Get stored auth token (deprecated - using Basic Auth)
   */
  private getToken(): string | null {
    return localStorage.getItem(API_CONFIG.TOKEN_KEY);
  }

  /**
   * Get stored refresh token (deprecated - using Basic Auth)
   */
  private getRefreshToken(): string | null {
    return localStorage.getItem(API_CONFIG.REFRESH_TOKEN_KEY);
  }

  /**
   * Get Basic Auth token (base64 encoded email:password)
   */
  private getBasicAuth(): string | null {
    return localStorage.getItem(API_CONFIG.BASIC_AUTH_TOKEN);
  }

  /**
   * Save Basic Auth token (base64 encoded email:password)
   */
  public saveBasicAuth(email: string, password: string): void {
    // Encode email:password to base64 and save as token
    const credentials = `${email}:${password}`;
    const base64Token = btoa(credentials);
    localStorage.setItem(API_CONFIG.BASIC_AUTH_TOKEN, base64Token);
  }

  /**
   * Save auth tokens (deprecated - using Basic Auth)
   */
  public saveTokens(token: string, refreshToken: string): void {
    localStorage.setItem(API_CONFIG.TOKEN_KEY, token);
    localStorage.setItem(API_CONFIG.REFRESH_TOKEN_KEY, refreshToken);
  }

  /**
   * Clear auth tokens and Basic Auth token
   */
  public clearTokens(): void {
    localStorage.removeItem(API_CONFIG.TOKEN_KEY);
    localStorage.removeItem(API_CONFIG.REFRESH_TOKEN_KEY);
    localStorage.removeItem(API_CONFIG.BASIC_AUTH_TOKEN);
    localStorage.removeItem(API_CONFIG.USER_KEY);
  }

 

  /**
   * Handle authentication errors
   */
  private handleAuthError(): void {
    this.clearTokens();
    // Redirect to login page
    window.location.href = '/login';
  }

  /**
   * Normalize error response
   */
  private normalizeError(error: AxiosError): ApiError {
    if (error.response) {
      // Server responded with error
      const data = error.response.data as any;
      return {
        message: data?.message || 'An error occurred',
        code: data?.code || 'UNKNOWN_ERROR',
        status: error.response.status,
        errors: data?.errors,
      };
    } else if (error.request) {
      // Request made but no response
      return {
        message: 'No response from server',
        code: 'NETWORK_ERROR',
        status: 0,
      };
    } else {
      // Request setup error
      return {
        message: error.message || 'Request failed',
        code: 'REQUEST_ERROR',
        status: 0,
      };
    }
  }

  /**
   * Make GET request
   */
  public async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    // Log complete request details RIGHT BEFORE calling axios
    const finalConfig = config || {};
    const fullUrl = `${this.client.defaults.baseURL || ''}${url}`;
    
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🚀 AXIOS GET REQUEST - RIGHT BEFORE SENDING');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('📍 URL:', url);
    console.log('🌐 Base URL:', this.client.defaults.baseURL);
    console.log('🔗 Full URL:', fullUrl);
    console.log('📋 Method: GET');
    console.log('⚙️  Config:', {
      params: finalConfig.params,
      headers: finalConfig.headers,
      timeout: finalConfig.timeout,
      ...finalConfig
    });
    console.log('📦 Headers:', finalConfig.headers || {});
    console.log('🔍 Query Params:', finalConfig.params || {});
    console.log('═══════════════════════════════════════════════════════════');
    
    const response: AxiosResponse<ApiResponse<T>> = await this.client.get(url, config);
    return response.data;
  }

  /**
   * Make POST request
   */
  public async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.post(url, data, config);
    return response.data;
  }

  /**
   * Make PUT request
   */
  public async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.put(url, data, config);
    return response.data;
  }

  /**
   * Make PATCH request
   */
  public async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.patch(url, data, config);
    return response.data;
  }

  /**
   * Make DELETE request
   */
  public async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.delete(url, config);
    return response.data;
  }

  /**
   * Upload file with progress tracking
   */
  public async uploadFile<T = any>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const response: AxiosResponse<ApiResponse<T>> = await this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  /**
   * Get raw axios instance for advanced usage
   */
  public getAxiosInstance(): AxiosInstance {
    return this.client;
  }
}

// Create and export singleton instance
const apiClient = new ApiClient();

export default apiClient;

// Extend AxiosRequestConfig to include metadata
declare module 'axios' {
  export interface AxiosRequestConfig {
    metadata?: {
      startTime: Date;
    };
  }
}

